// ModbusPage — kõikide pargi seadmete raw Modbus registrid.
// Park valik → 4 sektsiooni (PV mõõdik, GRID mõõdik, PCS, BAU) iga oma alarmidega.

import { useState, useMemo, useEffect } from 'react';
import { Header } from '../components/Layout';
import { useParks } from '../contexts/ParksContext';
import {
  Sun, Zap, Cpu, Battery, ChevronDown, ChevronRight,
  AlertOctagon, AlertTriangle, Info, CheckCircle2, Radio,
} from 'lucide-react';

// ---------- Registriaadressid (sama mis backend ParksService.js) ----------
// Format: { addr, key, unit, type, scale, regs?, fc, rw, notes? }
// rw: 'R' = ainult lugemine, 'R/W' = lugemine + kirjutamine
const METER_REGS = [
  { addr: 0x5002, key: 'v_l1',    unit: 'V',    type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x5004, key: 'v_l2',    unit: 'V',    type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x5006, key: 'v_l3',    unit: 'V',    type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x5008, key: 'hz',      unit: 'Hz',   type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x500C, key: 'i_l1',    unit: 'A',    type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x500E, key: 'i_l2',    unit: 'A',    type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x5010, key: 'i_l3',    unit: 'A',    type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x5012, key: 'p_total', unit: 'kW',   type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x501A, key: 'q_total', unit: 'kvar', type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x5022, key: 's_total', unit: 'kVA',  type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x502A, key: 'pf',      unit: '',     type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x6000, key: 'e_total', unit: 'kWh',  type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x600C, key: 'e_fwd',   unit: 'kWh',  type: 'float32', regs: 2, rw: 'R' },
  { addr: 0x6018, key: 'e_rev',   unit: 'kWh',  type: 'float32', regs: 2, rw: 'R' },
];

// PCS_REG_GROUPS — täielik PCS protokoll (IN-POWER 125kW V3.3.20).
// Pollitakse hetkel ainult osad registrid (vt ParksService.js); UI näitab spec'i kõik.
const PCS_REG_GROUPS = [
  {
    name: 'Coil registrid — kaugjuhtimise käsklused',
    addr: '0x00001..0x00012', fc: 'FC 01/05', slave: 'PCS slave',
    notes: 'Kirjutamine: FC 05 single coil (0xFF00=ON, 0x0000=OFF). Lugemine: FC 01.',
    regs: [
      { addr: 1,  key: 'fault_reset',                       type: 'Bool', scale: 1, rw: 'R/W', notes: '1 = lähtesta vead' },
      { addr: 2,  key: 'device_startup',                    type: 'Bool', scale: 1, rw: 'R/W', notes: '1 = käivita' },
      { addr: 3,  key: 'device_shutdown',                   type: 'Bool', scale: 1, rw: 'R/W', notes: '1 = seiska' },
      { addr: 4,  key: 'remote_emergency_stop',             type: 'Bool', scale: 1, rw: 'R/W', notes: '1 = HÄDAPEATUS (ettevaatust!)' },
      { addr: 5,  key: 'reset_accumulated_charging_power',  type: 'Bool', scale: 1, rw: 'R/W', notes: '1 = nulli kogu lae arvesti' },
      { addr: 6,  key: 'reset_accumulated_discharging_power', type: 'Bool', scale: 1, rw: 'R/W', notes: '1 = nulli kogu tühj arvesti' },
      { addr: 7,  key: 'remote_local_settings',             type: 'Bool', scale: 1, rw: 'R/W', notes: '1 = Remote, 0 = Local' },
      { addr: 8,  key: 'device_standby',                    type: 'Bool', scale: 1, rw: 'R/W', notes: '1 = Standby, 0 = Invalid' },
      { addr: 12, key: 'sts_remote_control',                type: 'Bool', scale: 1, rw: 'R/W', notes: '1 = STS Open (lülita välja), 0 = STS Close (sisse)' },
    ],
  },
  {
    name: 'Discrete Inputs — olekubitid',
    addr: '0x00081..0x00097', fc: 'FC 02', slave: 'PCS slave',
    notes: 'Read-only. Soovitus: lugeda 16 reg alates 0x81.',
    regs: [
      { addr: 81, key: 'shutdown_status',     type: 'Bool', scale: 1, rw: 'R', notes: '1 = Shutdown' },
      { addr: 82, key: 'standby_status',      type: 'Bool', scale: 1, rw: 'R', notes: '1 = Standby' },
      { addr: 83, key: 'running_status',      type: 'Bool', scale: 1, rw: 'R', notes: '1 = Running' },
      { addr: 84, key: 'total_fault_status',  type: 'Bool', scale: 1, rw: 'R', notes: '1 = Fault' },
      { addr: 85, key: 'total_alarm_status',  type: 'Bool', scale: 1, rw: 'R', notes: '1 = Alarm' },
      { addr: 86, key: 'remote_local_status', type: 'Bool', scale: 1, rw: 'R', notes: '1 = Remote, 0 = Local' },
      { addr: 87, key: 'emergency_stop_input', type: 'Bool', scale: 1, rw: 'R', notes: '1 = E-stop aktiivne' },
      { addr: 88, key: 'grid_connected',      type: 'Bool', scale: 1, rw: 'R', notes: '1 = Grid-connected' },
      { addr: 89, key: 'vf_grid_disconnected', type: 'Bool', scale: 1, rw: 'R', notes: '1 = VF off-grid' },
      { addr: 90, key: 'overload_derating',   type: 'Bool', scale: 1, rw: 'R', notes: '1 = Overload derating' },
      { addr: 94, key: 'bms_dry_contact',     type: 'Bool', scale: 1, rw: 'R' },
      { addr: 95, key: 'pos_dc_main_breaker', type: 'Bool', scale: 1, rw: 'R', notes: 'High-voltage box integration' },
      { addr: 96, key: 'neg_dc_main_breaker', type: 'Bool', scale: 1, rw: 'R', notes: 'High-voltage box integration' },
      { addr: 97, key: 'sts_switch_status',   type: 'Bool', scale: 1, rw: 'R' },
    ],
  },
  {
    name: 'AC telemeetria — pinged, voolud, võimsused',
    addr: '0x00201..0x00226', fc: 'FC 04', slave: 'PCS slave',
    regs: [
      { addr: 201, key: 'v_l1',     unit: 'V',    type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 202, key: 'v_l2',     unit: 'V',    type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 203, key: 'v_l3',     unit: 'V',    type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 204, key: 'i_l1',     unit: 'A',    type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 205, key: 'i_l2',     unit: 'A',    type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 206, key: 'i_l3',     unit: 'A',    type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 207, key: 'hz',       unit: 'Hz',   type: 'U16', scale: 0.01, rw: 'R' },
      { addr: 208, key: 'p_a',      unit: 'kW',   type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 209, key: 'p_b',      unit: 'kW',   type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 210, key: 'p_c',      unit: 'kW',   type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 211, key: 'p_total',  unit: 'kW',   type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 212, key: 'q_a',      unit: 'kVar', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 213, key: 'q_b',      unit: 'kVar', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 214, key: 'q_c',      unit: 'kVar', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 215, key: 'q_total',  unit: 'kvar', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 216, key: 's_a',      unit: 'kVA',  type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 217, key: 's_b',      unit: 'kVA',  type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 218, key: 's_c',      unit: 'kVA',  type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 219, key: 's_total',  unit: 'kVA',  type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 220, key: 'pf_a',     unit: '',     type: 'S16', scale: 0.001, rw: 'R' },
      { addr: 221, key: 'pf_b',     unit: '',     type: 'S16', scale: 0.001, rw: 'R' },
      { addr: 222, key: 'pf_c',     unit: '',     type: 'S16', scale: 0.001, rw: 'R' },
      { addr: 223, key: 'pf',       unit: '',     type: 'S16', scale: 0.001, rw: 'R', notes: 'Total PF' },
    ],
  },
  {
    name: 'DC + olek + heartbeat',
    addr: '0x00224..0x00247', fc: 'FC 04', slave: 'PCS slave',
    regs: [
      { addr: 224, key: 'dc_power',          unit: 'kW', type: 'S16', scale: 0.1, rw: 'R', notes: 'PCS DC input' },
      { addr: 225, key: 'dc_voltage',        unit: 'V',  type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 226, key: 'dc_current',        unit: 'A',  type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 227, key: 'pcs_radiator_temp', unit: '°C', type: 'S16', scale: 1, rw: 'R', notes: '125kW kasutab' },
      { addr: 228, key: 'bms_op_status',     unit: '',   type: 'U16', scale: 1, rw: 'R', notes: '125kW lisatud' },
      { addr: 229, key: 'pcs_status',        unit: '',   type: 'U16', scale: 1, rw: 'R', notes: '0=Shutdown,1=SelfCheck,2=Operation,4=Fault,7=Standby,10=Charging,11=Discharging' },
      { addr: 238, key: 'pcs_comm_status',   unit: '',   type: 'U16', scale: 1, rw: 'R', notes: 'Increment by 1 every 20ms (heartbeat)' },
      { addr: 247, key: 'phase_n_current',   unit: 'A',  type: 'U16', scale: 0.1, rw: 'R' },
    ],
  },
  {
    name: 'Energia akumulaatorid (32-bit)',
    addr: '0x00230..0x00237', fc: 'FC 04', slave: 'PCS slave',
    notes: 'Iga väärtus 32-bit (low+high reg, scale 0.001 kWh).',
    regs: [
      { addr: 230, key: 'pcs_ac_chg_low',  unit: 'kWh', type: 'U16', scale: 0.001, rw: 'R', notes: 'AC accum. charging low 16 bits' },
      { addr: 231, key: 'pcs_ac_chg_high', unit: 'kWh', type: 'U16', scale: 0.001, rw: 'R' },
      { addr: 232, key: 'pcs_ac_dsg_low',  unit: 'kWh', type: 'U16', scale: 0.001, rw: 'R' },
      { addr: 233, key: 'pcs_ac_dsg_high', unit: 'kWh', type: 'U16', scale: 0.001, rw: 'R' },
      { addr: 234, key: 'pcs_dc_chg_low',  unit: 'kWh', type: 'U16', scale: 0.001, rw: 'R' },
      { addr: 235, key: 'pcs_dc_chg_high', unit: 'kWh', type: 'U16', scale: 0.001, rw: 'R' },
      { addr: 236, key: 'pcs_dc_dsg_low',  unit: 'kWh', type: 'U16', scale: 0.001, rw: 'R' },
      { addr: 237, key: 'pcs_dc_dsg_high', unit: 'kWh', type: 'U16', scale: 0.001, rw: 'R' },
    ],
  },
  {
    name: 'BMS info (read-only / read-write sõltuvalt integratsioonist)',
    addr: '0x00280..0x00295', fc: 'FC 03/04', slave: 'PCS slave',
    notes: 'PCS+BMS sõltumatu side: FC 04 + read-only. Integreeritud: FC 03 + read-write.',
    regs: [
      { addr: 280, key: 'bms_op_command',         unit: 'hex', type: 'U16', scale: 1, rw: 'R/W',
        notes: '0x1111=chg disabled, 0x2222=dsg disabled, 0x5555=standby, 0xaaaa=fault, 0xbbbb=normal, 0xcccc=alarm' },
      { addr: 281, key: 'bms_chg_current_limit',  unit: 'A',   type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 282, key: 'bms_dsg_current_limit',  unit: 'A',   type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 283, key: 'bms_soc',                unit: '%',   type: 'U16', scale: 1, rw: 'R/W', notes: 'NB! skaala 1 (mitte 0.1)' },
      { addr: 284, key: 'bms_chargeable_kwh',     unit: 'kWh', type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 285, key: 'bms_dischargeable_kwh',  unit: 'kWh', type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 286, key: 'bms_total_voltage',      unit: 'V',   type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 287, key: 'bms_total_current',      unit: 'A',   type: 'S16', scale: 0.1, rw: 'R/W' },
      { addr: 288, key: 'bms_max_soc',            unit: '%',   type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 289, key: 'bms_min_soc',            unit: '%',   type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 290, key: 'bms_max_cell_voltage',   unit: 'V',   type: 'U16', scale: 0.001, rw: 'R/W' },
      { addr: 291, key: 'bms_min_cell_voltage',   unit: 'V',   type: 'U16', scale: 0.001, rw: 'R/W' },
      { addr: 292, key: 'bms_max_temp',           unit: '°C',  type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 293, key: 'bms_min_temp',           unit: '°C',  type: 'S16', scale: 0.1, rw: 'R/W' },
      { addr: 294, key: 'bms_allow_charge_power',    unit: 'kW', type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 295, key: 'bms_allow_discharge_power', unit: 'kW', type: 'U16', scale: 0.1, rw: 'R/W' },
    ],
  },
  {
    name: 'Holding registrid — kirjutuskäsklused (working setpoints)',
    addr: '0x00301..0x00345', fc: 'FC 03/06/16', slave: 'PCS slave',
    notes: 'KÕIK read-write. Kirjutus FC 06 (single) või FC 16 (multiple).',
    regs: [
      { addr: 301, key: 'running_mode',          unit: '',   type: 'U16', scale: 1,    rw: 'R/W', notes: '0=None, 1=Const I chg, 2=Const V chg, 3=Const Power, 4=DC const V (default 3)' },
      { addr: 302, key: 'voltage_setpoint',      unit: 'V',  type: 'U16', scale: 0.1, rw: 'R/W', notes: 'Constant voltage charging setpoint' },
      { addr: 303, key: 'current_setpoint',      unit: 'A',  type: 'S16', scale: 0.1, rw: 'R/W', notes: 'Negatiivne = tühjene, positiivne = lae (default 0)' },
      { addr: 304, key: 'active_power_setpoint', unit: 'kW', type: 'S16', scale: 0.1, rw: 'R/W', notes: 'Constant power 3-faasiline (negatiivne = tühj, positiivne = lae)' },
      { addr: 305, key: 'reactive_power_setpoint', unit: 'kVar', type: 'S16', scale: 0.1, rw: 'R/W', notes: 'Constant reactive (positive=induktiivne, negative=mahtuv)' },
      { addr: 306, key: 'grid_mode',             unit: '',   type: 'U16', scale: 1,   rw: 'R/W', notes: '0=On-grid, 1=VF off-grid, 2=VSG (default 0)' },
      { addr: 307, key: 'off_grid_voltage',      unit: 'V',  type: 'U16', scale: 1,   rw: 'R/W', notes: 'VF off-grid output voltage (default 230)' },
      { addr: 308, key: 'off_grid_frequency',    unit: 'Hz', type: 'U16', scale: 0.01, rw: 'R/W', notes: 'VF off-grid output Hz (default 50.00)' },
      { addr: 309, key: 'p_a_setpoint_split',    unit: 'kW', type: 'S16', scale: 0.1, rw: 'R/W', notes: 'Phase A active power split-phase' },
      { addr: 310, key: 'p_b_setpoint_split',    unit: 'kW', type: 'S16', scale: 0.1, rw: 'R/W' },
      { addr: 311, key: 'p_c_setpoint_split',    unit: 'kW', type: 'S16', scale: 0.1, rw: 'R/W' },
      { addr: 312, key: 'q_a_setpoint_split',    unit: 'kVar', type: 'S16', scale: 0.1, rw: 'R/W' },
      { addr: 313, key: 'q_b_setpoint_split',    unit: 'kVar', type: 'S16', scale: 0.1, rw: 'R/W' },
      { addr: 314, key: 'q_c_setpoint_split',    unit: 'kVar', type: 'S16', scale: 0.1, rw: 'R/W' },
      { addr: 315, key: 'off_grid_va',           unit: 'V',  type: 'U16', scale: 1,    rw: 'R/W', notes: 'Split-phase given A' },
      { addr: 316, key: 'off_grid_vb',           unit: 'V',  type: 'U16', scale: 1,    rw: 'R/W' },
      { addr: 317, key: 'off_grid_vc',           unit: 'V',  type: 'U16', scale: 1,    rw: 'R/W' },
      { addr: 319, key: 'primary_freq_dead_band', unit: 'Hz', type: 'U16', scale: 0.01, rw: 'R/W', notes: 'Range ±(0.03..0.05 Hz)' },
      { addr: 320, key: 'primary_freq_k_value',  unit: '%',  type: 'U16', scale: 0.1,  rw: 'R/W', notes: 'Range 0.5..3% — väärtus 11 = 1.1%' },
      { addr: 321, key: 'vsg_freq_droop',        unit: '',   type: 'U16', scale: 1,    rw: 'R/W', notes: 'Range 0..6368' },
      { addr: 323, key: 'vsg_voltage_droop',     unit: '',   type: 'U16', scale: 1,    rw: 'R/W', notes: 'Range 0..2000' },
      { addr: 324, key: 'sts_run_mode',          unit: '',   type: 'U16', scale: 1,    rw: 'R/W', notes: '0=None, 1=Manual, 2=Automatic, 3=Mix, 4=Silence' },
      { addr: 325, key: 'active_power_control_mode', unit: '', type: 'U16', scale: 1, rw: 'R/W', notes: '0=Split-phase, 1=Total power' },
      { addr: 330, key: 'time_sync_second',      unit: 's',  type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 331, key: 'time_sync_minute',      unit: 'min', type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 332, key: 'time_sync_hour',        unit: 'h',  type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 333, key: 'time_sync_day',         unit: '',   type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 334, key: 'time_sync_month',       unit: '',   type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 335, key: 'time_sync_year',        unit: '',   type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 336, key: 'reactive_power_mode',   unit: '',   type: 'U16', scale: 1, rw: 'R/W', notes: '0=Constant power mode, 1=Constant PF mode' },
      { addr: 337, key: 'power_factor_setting',  unit: '',   type: 'S16', scale: 0.001, rw: 'R/W', notes: 'Range -1.0..1.0' },
      { addr: 341, key: 'insulation_detection_enable', unit: '', type: 'U16', scale: 1, rw: 'R/W', notes: '0=Disabled, 1=Enabled' },
      { addr: 342, key: 'leakage_current_threshold', unit: 'A', type: 'U16', scale: 0.001, rw: 'R/W' },
      { addr: 343, key: 'insulation_resistance_threshold', unit: 'kΩ', type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 344, key: 'primary_freq_regulation_enable', unit: '', type: 'U16', scale: 1, rw: 'R/W', notes: '0=Disable, 1=Enable' },
      { addr: 345, key: 'off_grid_dsg_current_limit', unit: 'A', type: 'U16', scale: 1, rw: 'R/W' },
    ],
  },
  {
    name: 'Derating + DC + branch currents',
    addr: '0x00560..0x00574', fc: 'FC 04', slave: 'PCS slave',
    regs: [
      { addr: 560, key: 'derating_factor',    unit: '',   type: 'U16', scale: 1, rw: 'R' },
      { addr: 561, key: 'derating_indicator', unit: '',   type: 'U16', scale: 1, rw: 'R',
        notes: '1=Overvoltage, 2=Undervoltage, 3=Ambient temp, 4=Radiator overtemp, 5=EMS cmd, 6=BMS cmd' },
      { addr: 562, key: 'pcs_capacitor_upper_dc_v', unit: 'V',  type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 563, key: 'pcs_capacitor_lower_dc_v', unit: 'V',  type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 564, key: 'pcs_total_dc_voltage',     unit: 'V',  type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 565, key: 'leakage_current',          unit: 'A',  type: 'U16', scale: 0.001, rw: 'R' },
      { addr: 566, key: 'insulation_resistance',    unit: 'kΩ', type: 'U16', scale: 1,    rw: 'R' },
      { addr: 567, key: 'phase_a_branch1_i', unit: 'A', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 568, key: 'phase_b_branch1_i', unit: 'A', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 569, key: 'phase_c_branch1_i', unit: 'A', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 570, key: 'phase_n_branch1_i', unit: 'A', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 571, key: 'phase_a_branch2_i', unit: 'A', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 572, key: 'phase_b_branch2_i', unit: 'A', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 573, key: 'phase_c_branch2_i', unit: 'A', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 574, key: 'phase_n_branch2_i', unit: 'A', type: 'S16', scale: 0.1, rw: 'R' },
    ],
  },
  {
    name: 'Temperatuurid (faasid + ümbritsev)',
    addr: '0x00600..0x00608', fc: 'FC 04', slave: 'PCS slave',
    notes: '125kW lisatud',
    regs: [
      { addr: 600, key: 'phase_a_temp', unit: '°C', type: 'S16', scale: 0.01, rw: 'R' },
      { addr: 601, key: 'phase_b_temp', unit: '°C', type: 'S16', scale: 0.01, rw: 'R' },
      { addr: 602, key: 'phase_c_temp', unit: '°C', type: 'S16', scale: 0.01, rw: 'R' },
      { addr: 603, key: 'phase_n_temp', unit: '°C', type: 'S16', scale: 0.01, rw: 'R' },
      { addr: 604, key: 'air_intake_temp', unit: '°C', type: 'S16', scale: 0.01, rw: 'R' },
      { addr: 605, key: 'internal_chamber_temp', unit: '°C', type: 'S16', scale: 0.01, rw: 'R' },
      { addr: 606, key: 'air_exhaust_temp', unit: '°C', type: 'S16', scale: 0.01, rw: 'R' },
      { addr: 607, key: 'control_chip_temp', unit: '°C', type: 'S16', scale: 0.01, rw: 'R' },
      { addr: 608, key: 'comm_chip_temp', unit: '°C', type: 'S16', scale: 0.01, rw: 'R' },
    ],
  },
  {
    name: 'Fault sõnad (PCS + DCDC + control + comm)',
    addr: '0x00256, 0x00272..0x00279, 0x00609..0x00620', fc: 'FC 04', slave: 'PCS slave',
    notes: 'Bittide tähendused (Fault Word 1 nt): bit0..15 — vt PCS.pdf Appendix.',
    regs: [
      { addr: 256, key: 'pcs_fault_word_5',  unit: 'hex', type: 'U16', scale: 1, rw: 'R', notes: '125kW kasutab' },
      { addr: 272, key: 'pcs_fault_word_1',  unit: 'hex', type: 'U16', scale: 1, rw: 'R', notes: '125kW pole kasutusel' },
      { addr: 273, key: 'pcs_fault_word_2',  unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 274, key: 'pcs_fault_word_3',  unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 275, key: 'pcs_fault_word_4',  unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 276, key: 'dcdc_fault_word_1', unit: 'hex', type: 'U16', scale: 1, rw: 'R', notes: 'Vajab DCDC süsteemi' },
      { addr: 277, key: 'dcdc_fault_word_2', unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 278, key: 'dcdc_fault_word_3', unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 279, key: 'dcdc_fault_word_4', unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 609, key: 'control_fault_word_1', unit: 'hex', type: 'U16', scale: 1, rw: 'R',
        notes: 'bit0-15: Phase A/B/C/N hardware overcurrent, AC-DC mismatch, aux power outage, low temp/sensor, insulation, drives, radiator overtemp, ambient overtemp' },
      { addr: 610, key: 'control_fault_word_2', unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 611, key: 'control_fault_word_3', unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 612, key: 'control_fault_word_4', unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 613, key: 'control_fault_word_5', unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 617, key: 'comm_fault_word_1', unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 618, key: 'comm_fault_word_2', unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 619, key: 'comm_fault_word_3', unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
      { addr: 620, key: 'comm_fault_word_4', unit: 'hex', type: 'U16', scale: 1, rw: 'R' },
    ],
  },
  {
    name: 'PCS/BMS info, SN, versioonid, akumulaatorid',
    addr: '0x00501..0x00554', fc: 'FC 03', slave: 'PCS slave',
    regs: [
      { addr: 501, key: 'pcs_sn_1',  unit: 'char', type: 'U16', scale: 1, rw: 'R', notes: 'PCS Serial Number — 15 reg (501..515)' },
      { addr: 516, key: 'bms_sn_1',  unit: 'char', type: 'U16', scale: 1, rw: 'R', notes: 'BMS SN — 8 reg (516..523)' },
      { addr: 524, key: 'bms_sw_ver_1', unit: '', type: 'U16', scale: 1, rw: 'R', notes: '8 reg' },
      { addr: 532, key: 'bms_ctrl_status_1', unit: '', type: 'U16', scale: 1, rw: 'R', notes: '4 reg' },
      { addr: 536, key: 'cumulative_charging_cycles_low',  unit: '', type: 'U16', scale: 1, rw: 'R', notes: '32-bit (low+high)' },
      { addr: 537, key: 'cumulative_charging_cycles_high', unit: '', type: 'U16', scale: 1, rw: 'R' },
      { addr: 538, key: 'daily_charging_capacity_low',     unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 539, key: 'daily_charging_capacity_high',    unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 540, key: 'daily_discharging_capacity_low',  unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 541, key: 'daily_discharging_capacity_high', unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 542, key: 'annual_charging_capacity_low',    unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 543, key: 'annual_charging_capacity_high',   unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 544, key: 'annual_discharging_capacity_low', unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 545, key: 'annual_discharging_capacity_high', unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 546, key: 'daily_charging_duration',  unit: 'h', type: 'U16', scale: 1, rw: 'R' },
      { addr: 547, key: 'daily_discharging_duration', unit: 'h', type: 'U16', scale: 1, rw: 'R' },
      { addr: 548, key: 'annual_charging_duration',   unit: 'h', type: 'U16', scale: 1, rw: 'R' },
      { addr: 549, key: 'annual_discharging_duration', unit: 'h', type: 'U16', scale: 1, rw: 'R' },
      { addr: 550, key: 'total_charging_duration',   unit: 'h', type: 'U16', scale: 1, rw: 'R' },
      { addr: 551, key: 'total_discharging_duration', unit: 'h', type: 'U16', scale: 1, rw: 'R' },
      { addr: 552, key: 'total_operating_duration',  unit: 'h', type: 'U16', scale: 1, rw: 'R' },
      { addr: 553, key: 'control_sw_branch_version',  unit: '', type: 'U16', scale: 1, rw: 'R' },
      { addr: 554, key: 'comm_sw_branch_version',     unit: '', type: 'U16', scale: 1, rw: 'R' },
    ],
  },
];

// BAU registrigrupid (BAU.MD spec sec 9-15) — iga väli detailse aadressi + tüübi + skaala + R/W'iga
const BAU_REG_GROUPS = [
  {
    name: 'Bank Measure (mõõtmised)', addr: '0x2001..0x200A', fc: 'FC 03', slave: 'BAU 0x01',
    regs: [
      { addr: 0x2001, key: 'bank_voltage',       unit: 'V',   type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x2002, key: 'bank_current',       unit: 'A',   type: 'S32', scale: 0.1, regs: 2, rw: 'R', notes: '32-bit signed (high+low reg)' },
      { addr: 0x2004, key: 'soc',                unit: '%',   type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x2005, key: 'soh',                unit: '%',   type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x2006, key: 'insulation_resistance', unit: 'kΩ', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x2007, key: 'energy_chg_available', unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x2008, key: 'energy_dsg_available', unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x2009, key: 'current_chg_limit',  unit: 'A',   type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x200A, key: 'current_dsg_limit',  unit: 'A',   type: 'U16', scale: 0.1, rw: 'R' },
    ],
  },
  {
    name: 'Bank Signal (alarmid + faultid)', addr: '0x1001..0x1004', fc: 'FC 03', slave: 'BAU 0x01',
    regs: [
      { addr: 0x1001, key: 'fault_state',  unit: 'hex', type: 'U16', scale: 1, rw: 'R', notes: 'Bittide tähendused: vt BAU.MD sec 9' },
      { addr: 0x1002, key: 'alarm_level3', unit: 'hex', type: 'U16', scale: 1, rw: 'R', notes: 'Kriitilised alarmid' },
      { addr: 0x1003, key: 'alarm_level2', unit: 'hex', type: 'U16', scale: 1, rw: 'R', notes: 'Hoiatused' },
      { addr: 0x1004, key: 'alarm_level1', unit: 'hex', type: 'U16', scale: 1, rw: 'R', notes: 'Info-alarmid' },
    ],
  },
  {
    name: 'Run state (käituselusolek)', addr: '0x1031..0x1034', fc: 'FC 03', slave: 'BAU 0x01',
    regs: [
      { addr: 0x1031, key: 'run_state',     unit: '', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x1032, key: 'chg_dsg_state', unit: '', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x1034, key: 'heartbeat',     unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'kasvav iga 20ms' },
    ],
  },
  {
    name: 'Cell statistics (raku V/T)', addr: '0x200D..0x201E', fc: 'FC 03', slave: 'BAU 0x01',
    regs: [
      { addr: 0x2010, key: 'vcell_max', unit: 'mV',  type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x2014, key: 'vcell_min', unit: 'mV',  type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x2015, key: 'vcell_avg', unit: 'mV',  type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x2019, key: 'tcell_max', unit: '°C', type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x201D, key: 'tcell_min', unit: '°C', type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x201E, key: 'tcell_avg', unit: '°C', type: 'S16', scale: 0.1, rw: 'R' },
    ],
  },
  {
    name: 'Energy stats today (päevased)', addr: '0x2028..0x2029', fc: 'FC 03', slave: 'BAU 0x01',
    regs: [
      { addr: 0x2028, key: 'energy_dsg_today', unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x2029, key: 'energy_chg_today', unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
    ],
  },
  {
    name: 'PowerEnv — Energy meter (V/I/P)', addr: '0x51..0x59', fc: 'FC 03', slave: 'PowerEnv 0x7F',
    regs: [
      { addr: 0x51, key: 'meter_energy_fwd_high', unit: 'kWh', type: 'U16', scale: 1, rw: 'R', notes: '32-bit kombineeritud (high+low)' },
      { addr: 0x52, key: 'meter_energy_fwd_low',  unit: 'kWh', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x53, key: 'meter_voltage_a', unit: 'V', type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x54, key: 'meter_voltage_b', unit: 'V', type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x55, key: 'meter_voltage_c', unit: 'V', type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x56, key: 'meter_current_a', unit: 'A', type: 'U16', scale: 0.1, rw: 'R', notes: 'NB! skaala 0.1 (doc ütleb 0.01, on vale)' },
      { addr: 0x57, key: 'meter_current_b', unit: 'A', type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x58, key: 'meter_current_c', unit: 'A', type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x59, key: 'meter_power_total', unit: 'W', type: 'S16', scale: 1, rw: 'R', notes: 'doc unit "kW" + scale 0.001 → raw on W' },
    ],
  },
  {
    name: 'PowerEnv — Energy meter (faasid + Q/PF/Hz)', addr: '0x66..0x6C', fc: 'FC 03', slave: 'PowerEnv 0x7F',
    regs: [
      { addr: 0x66, key: 'meter_power_a',     unit: 'kW',   type: 'S16', scale: 0.001, rw: 'R' },
      { addr: 0x67, key: 'meter_power_b',     unit: 'kW',   type: 'S16', scale: 0.001, rw: 'R' },
      { addr: 0x68, key: 'meter_power_c',     unit: 'kW',   type: 'S16', scale: 0.001, rw: 'R' },
      { addr: 0x6A, key: 'meter_reactive_power', unit: 'kvar', type: 'S16', scale: 0.001, rw: 'R' },
      { addr: 0x6B, key: 'meter_pf',          unit: '',     type: 'S16', scale: 0.01, rw: 'R' },
      { addr: 0x6C, key: 'meter_frequency',   unit: 'Hz',   type: 'U16', scale: 0.01, rw: 'R' },
    ],
  },
  {
    name: 'PowerEnv — Sisekliima', addr: '0x4F..0x61', fc: 'FC 03', slave: 'PowerEnv 0x7F',
    regs: [
      { addr: 0x4F, key: 'bms_max_temp_env', unit: '°C',  type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 0x50, key: 'bms_min_temp_env', unit: '°C',  type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 0x5A, key: 'meter_temperature', unit: '°C', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 0x5B, key: 'meter_humidity',    unit: '%RH', type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x5C, key: 'dehum_humidity_start', unit: '%', type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 0x5D, key: 'dehum_humidity_stop',  unit: '%', type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 0x5E, key: 'heat_start_value', unit: '°C', type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 0x5F, key: 'heat_state',       unit: '',   type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x60, key: 'dehum_state',      unit: '',   type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x61, key: 'dehum_auto_mode',  unit: '',   type: 'U16', scale: 1, rw: 'R/W' },
    ],
  },
  {
    name: 'PowerEnv — Vedelikjahutus juhtimine + olek', addr: '0x0B..0x4E', fc: 'FC 03', slave: 'PowerEnv 0x7F',
    regs: [
      { addr: 0x0B, key: 'lc_system_on_off',          unit: '',   type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 0x0C, key: 'lc_operational_mode',       unit: '',   type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 0x0D, key: 'lc_temp_matching',          unit: '',   type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 0x0E, key: 'lc_refrigeration_point',    unit: '°C', type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 0x0F, key: 'lc_heating_point',          unit: '°C', type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 0x10, key: 'lc_refrig_return_diff',     unit: '°C', type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 0x11, key: 'lc_heating_return_diff',    unit: '°C', type: 'U16', scale: 0.1, rw: 'R/W' },
      { addr: 0x12, key: 'lc_pump_gear',              unit: '',   type: 'U16', scale: 1, rw: 'R/W' },
      { addr: 0x15, key: 'lc_effluent_temp',          unit: '°C', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 0x16, key: 'lc_backwater_temp',         unit: '°C', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 0x17, key: 'lc_exhaust_temp',           unit: '°C', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 0x18, key: 'lc_ambient_temp',           unit: '°C', type: 'S16', scale: 0.1, rw: 'R' },
      { addr: 0x19, key: 'lc_backwater_pressure',     unit: 'bar', type: 'U16', scale: 0.01, rw: 'R' },
      { addr: 0x1A, key: 'lc_effluent_pressure',      unit: 'bar', type: 'U16', scale: 0.01, rw: 'R' },
      { addr: 0x47, key: 'lc_pump_speed',             unit: '%',  type: 'U16', scale: 0.1, rw: 'R' },
      { addr: 0x48, key: 'lc_pump_state',             unit: '',   type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x49, key: 'lc_compressor_state',       unit: '',   type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x4A, key: 'lc_system_mode',            unit: '',   type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x4B, key: 'lc_unit_state',             unit: '',   type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x4C, key: 'lc_electric_heating_state', unit: '',   type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x4D, key: 'lc_external_fan_speed',     unit: '',   type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x4E, key: 'lc_compressor_speed',       unit: '',   type: 'U16', scale: 1, rw: 'R' },
    ],
  },
  {
    name: 'PowerEnv — Vedelikjahutuse fault registrid', addr: '0x1B..0x46', fc: 'FC 03', slave: 'PowerEnv 0x7F',
    regs: [
      { addr: 0x1B, key: 'lc_high_effluent_temp',     unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Vee väljavoolu kõrge temp' },
      { addr: 0x1D, key: 'lc_outlet_temp_sensor_fault', unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Vee väljavoolu temp-anduri viga' },
      { addr: 0x1E, key: 'lc_return_temp_sensor_fault', unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Vee tagasivoolu temp-anduri viga' },
      { addr: 0x20, key: 'lc_compressor_comm_fault',  unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Kompressori draiveri sidetõrge' },
      { addr: 0x21, key: 'lc_high_pressure_lock',     unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Süsteemi kõrgrõhu lukk' },
      { addr: 0x22, key: 'lc_low_voltage_lock',       unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Süsteemi madalpinge lukk' },
      { addr: 0x2A, key: 'lc_alarm_highest_level',    unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Praegune kõrgeim alarmitase' },
      { addr: 0x2B, key: 'lc_make_up_tank_low',       unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Lisaveepaagi madal tase' },
      { addr: 0x2C, key: 'lc_water_shortage',         unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Veepuudus hoiatus' },
      { addr: 0x39, key: 'lc_pump_comm_fault',        unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Veepumba draiveri sidetõrge' },
      // ... (täielik nimekiri 44 fault biti — vt BAU.MD sec 13)
    ],
    notes: 'Kõik fault bitid 0x1B..0x46. Iga reg = 0 (OK) või mitte-0 (aktiivne). Täielik nimekiri vt BAU.MD spec sec 13.',
  },
  {
    name: 'PowerEnv — Side-faults & DI sisendid', addr: '0x01..0x0A', fc: 'FC 03', slave: 'PowerEnv 0x7F',
    regs: [
      { addr: 0x01, key: 'humTemp_comm_fault',        unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'HumTemp anduri sidetõrge' },
      { addr: 0x02, key: 'liquid_cooling_comm_fault', unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Vedelikjahutuse sidetõrge' },
      { addr: 0x03, key: 'energy_meter_comm_fault',   unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Energy meter sidetõrge' },
      { addr: 0x04, key: 'di1H',  unit: '', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x05, key: 'di7L',  unit: '', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x06, key: 'di8L',  unit: '', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x07, key: 'di9L',  unit: '', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x08, key: 'di10L', unit: '', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x09, key: 'di11L', unit: '', type: 'U16', scale: 1, rw: 'R' },
      { addr: 0x0A, key: 'tempAndSmokeAlarm', unit: '', type: 'U16', scale: 1, rw: 'R', notes: 'Suitsu/temp alarm' },
    ],
  },
];

// ============================================================================
// Inverter registrigrupid — kõikidele toetatud tüüpidele.
// Goodwe GW50K-MT (custom poller, mitte Telegraf), Huawei SUN2000 (Telegraf modbus).
// ============================================================================

// Goodwe GW50K-MT — IN-POWER protocol (sama mis 125kW PCS), aga registritega 768-855.
const GOODWE_GW50K_REGS = [
  { addr: 768, key: 'pv12',           unit: 'PV1+PV2 (V/I)', type: 'PV pair', regs: 6, rw: 'R', notes: 'PV1_V@768 (U16 0.1V) + PV1_I@769 (S16 0.01A); PV2 same' },
  { addr: 781, key: 'pac',            unit: 'W',  type: 'UINT16', scale: 1, rw: 'R', notes: 'Active power (AC output)' },
  { addr: 782, key: 'work_mode',      unit: '',   type: 'UINT16', scale: 1, rw: 'R', notes: '0=Wait, 1=Run, 2=Fault, 3=Standby' },
  { addr: 783, key: 'internal_temp',  unit: '°C', type: 'INT16',  scale: 0.1, rw: 'R' },
  { addr: 784, key: 'error_word',     unit: 'hex', type: 'UINT32', regs: 2, rw: 'R', notes: '32-bit error bitfield (32 bits, vt Goodwe spec)' },
  { addr: 786, key: 'e_total',        unit: 'kWh', type: 'UINT32', regs: 2, rw: 'R' },
  { addr: 800, key: 'e_day',          unit: 'kWh', type: 'UINT16', scale: 0.1, rw: 'R' },
  { addr: 855, key: 'pv34',           unit: 'PV3+PV4 (V/I)', type: 'PV pair', regs: 6, rw: 'R' },
  { addr: 772, key: 'ac_v',           unit: 'V',  type: 'UINT16 x3', scale: 0.1, rw: 'R', notes: '772=A, 773=B, 774=C' },
  { addr: 775, key: 'ac_i',           unit: 'A',  type: 'UINT16 x3', scale: 0.1, rw: 'R', notes: '775=A, 776=B, 777=C' },
  { addr: 778, key: 'ac_hz',          unit: 'Hz', type: 'UINT16', scale: 0.01, rw: 'R' },
];

// Huawei SUN2000 M-series (60KTL-M0) — V500R001 register map
const HUAWEI_M_SERIES_REGS = [
  { addr: 32064, key: 'active_power_in', unit: 'kW',   type: 'INT32',  scale: 0.001, rw: 'R', notes: 'Input power (DC, M-series)' },
  { addr: 32066, key: 'reactive_power',  unit: 'kvar', type: 'INT32',  scale: 0.001, rw: 'R' },
  { addr: 32069, key: 'voltage_a',       unit: 'V',    type: 'UINT16', scale: 0.1,   rw: 'R' },
  { addr: 32070, key: 'voltage_b',       unit: 'V',    type: 'UINT16', scale: 0.1,   rw: 'R' },
  { addr: 32071, key: 'voltage_c',       unit: 'V',    type: 'UINT16', scale: 0.1,   rw: 'R' },
  { addr: 32072, key: 'current_a',       unit: 'A',    type: 'INT32',  scale: 0.001, rw: 'R' },
  { addr: 32074, key: 'current_b',       unit: 'A',    type: 'INT32',  scale: 0.001, rw: 'R' },
  { addr: 32076, key: 'current_c',       unit: 'A',    type: 'INT32',  scale: 0.001, rw: 'R' },
  { addr: 32080, key: 'pac',             unit: 'kW',   type: 'INT32',  scale: 0.001, rw: 'R', notes: 'Active power (AC output)' },
  { addr: 32084, key: 'power_factor',    unit: '',     type: 'INT16',  scale: 0.001, rw: 'R' },
  { addr: 32085, key: 'grid_freq',       unit: 'Hz',   type: 'UINT16', scale: 0.01,  rw: 'R' },
  { addr: 32087, key: 'internal_temp',   unit: '°C',   type: 'INT16',  scale: 0.1,   rw: 'R' },
  { addr: 32088, key: 'insulation_res',  unit: 'MΩ',   type: 'UINT16', scale: 0.001, rw: 'R' },
  { addr: 32089, key: 'device_status',   unit: 'hex',  type: 'UINT16', scale: 1,     rw: 'R', notes: '0x0000=Standby init, 0x0200=On-grid, 0x0300=Shutdown, 0x0500=Fault, 0xA000=No irradiation' },
  { addr: 32106, key: 'e_total',         unit: 'kWh',  type: 'UINT32', scale: 0.01,  rw: 'R' },
  { addr: 32114, key: 'e_day',           unit: 'kWh',  type: 'UINT32', scale: 0.01,  rw: 'R' },
];

const HUAWEI_M_SERIES_PV = [
  { addr: 32016, key: 'pv1_v', unit: 'V', type: 'UINT16', scale: 0.1, rw: 'R' },
  { addr: 32017, key: 'pv1_i', unit: 'A', type: 'INT16',  scale: 0.01, rw: 'R' },
  { addr: 32018, key: 'pv2_v', unit: 'V', type: 'UINT16', scale: 0.1, rw: 'R' },
  { addr: 32019, key: 'pv2_i', unit: 'A', type: 'INT16',  scale: 0.01, rw: 'R' },
  { addr: 32020, key: 'pv3_v', unit: 'V', type: 'UINT16', scale: 0.1, rw: 'R' },
  { addr: 32021, key: 'pv3_i', unit: 'A', type: 'INT16',  scale: 0.01, rw: 'R' },
  { addr: 32022, key: 'pv4_v', unit: 'V', type: 'UINT16', scale: 0.1, rw: 'R' },
  { addr: 32023, key: 'pv4_i', unit: 'A', type: 'INT16',  scale: 0.01, rw: 'R' },
];

// Huawei SUN2000 V2-series (20KTL, 33KTL) — V100R001 / V200R002 register map
const HUAWEI_V2_SERIES_REGS = [
  { addr: 32277, key: 'voltage_a',       unit: 'V',    type: 'UINT16', scale: 0.1,   rw: 'R' },
  { addr: 32278, key: 'voltage_b',       unit: 'V',    type: 'UINT16', scale: 0.1,   rw: 'R' },
  { addr: 32279, key: 'voltage_c',       unit: 'V',    type: 'UINT16', scale: 0.1,   rw: 'R' },
  { addr: 32280, key: 'current_a',       unit: 'A',    type: 'UINT16', scale: 0.1,   rw: 'R' },
  { addr: 32281, key: 'current_b',       unit: 'A',    type: 'UINT16', scale: 0.1,   rw: 'R' },
  { addr: 32282, key: 'current_c',       unit: 'A',    type: 'UINT16', scale: 0.1,   rw: 'R' },
  { addr: 32283, key: 'grid_freq',       unit: 'Hz',   type: 'UINT16', scale: 0.01,  rw: 'R' },
  { addr: 32284, key: 'power_factor',    unit: '',     type: 'INT16',  scale: 0.001, rw: 'R' },
  { addr: 32285, key: 'efficiency',      unit: '%',    type: 'UINT16', scale: 0.01,  rw: 'R' },
  { addr: 32286, key: 'internal_temp',   unit: '°C',   type: 'INT16',  scale: 0.1,   rw: 'R' },
  { addr: 32287, key: 'device_status',   unit: 'hex',  type: 'UINT16', scale: 1,     rw: 'R', notes: '0x0000=Standby init, 0x0200=On-grid, 0x0300=Shutdown, 0x0500=Fault, 0xA000=No irradiation' },
  { addr: 32290, key: 'pac',             unit: 'kW',   type: 'INT32',  scale: 0.001, rw: 'R', notes: 'Active power' },
  { addr: 32292, key: 'reactive_power',  unit: 'kvar', type: 'INT32',  scale: 0.001, rw: 'R' },
  { addr: 32300, key: 'e_day',           unit: 'kWh',  type: 'UINT32', scale: 0.01,  rw: 'R' },
  { addr: 32306, key: 'e_total',         unit: 'kWh',  type: 'UINT32', scale: 0.01,  rw: 'R' },
  { addr: 32323, key: 'insulation_res',  unit: 'MΩ',   type: 'UINT16', scale: 0.001, rw: 'R' },
];

const HUAWEI_V2_SERIES_PV = [
  { addr: 32262, key: 'pv1_v', unit: 'V', type: 'UINT16', scale: 0.1, rw: 'R' },
  { addr: 32263, key: 'pv1_i', unit: 'A', type: 'INT16',  scale: 0.01, rw: 'R' },
  { addr: 32264, key: 'pv2_v', unit: 'V', type: 'UINT16', scale: 0.1, rw: 'R' },
  { addr: 32265, key: 'pv2_i', unit: 'A', type: 'INT16',  scale: 0.01, rw: 'R' },
  { addr: 32266, key: 'pv3_v', unit: 'V', type: 'UINT16', scale: 0.1, rw: 'R' },
  { addr: 32267, key: 'pv3_i', unit: 'A', type: 'INT16',  scale: 0.01, rw: 'R' },
  { addr: 32268, key: 'pv4_v', unit: 'V', type: 'UINT16', scale: 0.1, rw: 'R' },
  { addr: 32269, key: 'pv4_i', unit: 'A', type: 'INT16',  scale: 0.01, rw: 'R' },
];

// Tagasta inverter-tüübile vastavad register-grupid
function inverterRegGroups(inv) {
  const t = inv?.type || '';
  if (t.startsWith('GW50K')) {
    return [{
      name: 'Goodwe GW50K-MT (IN-POWER 50kW)', addr: '768..855', fc: 'FC 03', slave: `slave ${inv.slave_id}`,
      regs: GOODWE_GW50K_REGS,
      notes: 'Custom Goodwe poller (GoodweInverterPoller.js) loeb iga 10s. Telegraf ei tööta — Goodwe sulgeb TCP iga vastuse järel.',
    }];
  }
  if (t === 'SUN2000-60KTL-M0' || t.includes('-M0')) {
    return [
      { name: 'Huawei M-series telemeetria', addr: '32064..32114', fc: 'FC 03', slave: `slave ${inv.slave_id}`,
        regs: HUAWEI_M_SERIES_REGS, notes: 'V500R001 register map' },
      { name: 'Huawei M-series PV stringid', addr: '32016..32023', fc: 'FC 03', slave: `slave ${inv.slave_id}`,
        regs: HUAWEI_M_SERIES_PV, notes: 'Iga PV string V+I paaris' },
    ];
  }
  if (t === 'SUN2000-20KTL' || t === 'SUN2000-33KTL' || t.includes('KTL')) {
    return [
      { name: 'Huawei V2-series telemeetria', addr: '32277..32323', fc: 'FC 03', slave: `slave ${inv.slave_id}`,
        regs: HUAWEI_V2_SERIES_REGS, notes: 'V100R001 / V200R002 register map' },
      { name: 'Huawei V2-series PV stringid', addr: '32262..32269', fc: 'FC 03', slave: `slave ${inv.slave_id}`,
        regs: HUAWEI_V2_SERIES_PV },
    ];
  }
  return [{
    name: 'Tundmatu inverter — vt spec', addr: '?', fc: 'FC 03', slave: `slave ${inv.slave_id}`,
    regs: [], notes: `Inverter tüüp "${t}" ei oma defineeritud register-mappi`,
  }];
}

function fmtVal(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') {
    return v.toLocaleString('et-EE', { maximumFractionDigits: 4 });
  }
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 40);
  return String(v);
}

// =====================================================================
export function ModbusPage({ wsConnected }) {
  const { data } = useParks();
  const onlineParks = useMemo(() =>
    (data?.parks || []).filter(p => p.name).map(p => ({ slot: p.slot, name: p.name, online: p.online })),
    [data]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  // Vali esimene nähtav park (kliendi vaates pole nt slot 12)
  useEffect(() => {
    if (onlineParks.length && !onlineParks.some(p => p.slot === selectedSlot)) setSelectedSlot(onlineParks[0].slot);
  }, [onlineParks, selectedSlot]);

  const park = (data?.parks || []).find(p => p.slot === selectedSlot);
  const isDeye = park?.meter_protocol === 'deye_v5';

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg)' }}>
      <Header
        title="Modbus toorväärtused"
        connected={wsConnected}
        lastUpdate={data?.updated ? new Date(data.updated).getTime() : null}
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 40px', maxWidth: 1480, width: '100%', margin: '0 auto' }}>
        {/* Park valik */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '10px 14px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Park
          </span>
          <select
            value={selectedSlot ?? ''}
            onChange={(e) => setSelectedSlot(parseInt(e.target.value))}
            style={{
              padding: '5px 10px', borderRadius: 4,
              border: '1px solid var(--border-strong)', background: 'white',
              fontSize: '0.78rem', fontFamily: 'inherit', minWidth: 220,
            }}
          >
            {onlineParks.length === 0 && <option>—</option>}
            {onlineParks.map(p => (
              <option key={p.slot} value={p.slot}>
                Slot {p.slot} · {p.name} {p.online ? '' : '(offline)'}
              </option>
            ))}
          </select>
          {park && (
            <span style={{ fontSize: '0.66rem', color: 'var(--text-3)', fontFamily: 'JetBrains Mono, monospace' }}>
              {park.online ? '● online' : '○ offline'} · {isDeye ? 'Deye · Solarman V5' : 'ex9EMS'} @ {park.host}
            </span>
          )}
        </div>

        {!park && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-3)' }}>
            Vali park ülevalt.
          </div>
        )}

        {park && isDeye && <DeyeRegisterView slot={park.slot} />}

        {park && !isDeye && (
          <>
            <DeviceSection
              icon={<Sun size={14} style={{ color: '#ca8a04' }} />}
              title="Päikesejaam (PV)"
              meta={`ex9EMS @ ${park.host} · slave ${park.slave_solar ?? 1} · FC 03`}
              online={!!park.solar}
              alarms={park.solar ? [] : [{ severity: 'warning', text: 'Mõõdik ei vastanud päringule' }]}
              groups={[{
                name: 'Mõõdiku registrid', addr: '0x5002..0x6018', fc: 'FC 03',
                slave: `slave ${park.slave_solar ?? 1}`,
                regs: METER_REGS, values: park.solar || {},
              }]}
            />

            <DeviceSection
              icon={<Zap size={14} style={{ color: '#1d4ed8' }} />}
              title="Võrk (GRID)"
              meta={`ex9EMS @ ${park.host} · slave ${park.slave_grid ?? 2} · FC 03`}
              online={!!park.grid}
              alarms={park.grid ? [] : [{ severity: 'warning', text: 'Mõõdik ei vastanud päringule' }]}
              groups={[{
                name: 'Mõõdiku registrid', addr: '0x5002..0x6018', fc: 'FC 03',
                slave: `slave ${park.slave_grid ?? 2}`,
                regs: METER_REGS, values: park.grid || {},
              }]}
            />

            <DeviceSection
              icon={<Cpu size={14} style={{ color: '#7c3aed' }} />}
              title="PCS (IN-POWER 125kW V3.3.20)"
              meta={park.pcs_host ? `@ ${park.pcs_host} · slave ${park.pcs_slave ?? 1} · 100M Ethernet, port 502` : 'PCS pole konfigureeritud'}
              online={park.pcs_online}
              alarms={park.pcs_host && !park.pcs_online ? [{ severity: 'warning', text: 'PCS ei vasta päringutele' }] : []}
              groups={park.pcs_host ? PCS_REG_GROUPS.map(g => ({
                ...g, values: park.pcs || {},
              })) : []}
            />

            <DeviceSection
              icon={<Battery size={14} style={{ color: '#15803d' }} />}
              title="BAU + PowerEnv"
              meta={park.bau_host
                ? `BAU @ ${park.bau_host}:${park.bau_port ?? 12230} · slave ${park.bau_slave ?? 1} (BAU 0x01) + 0x${(0x7E + (park.bau_slave ?? 1)).toString(16).toUpperCase()} (PowerEnv)`
                : 'BAU pole konfigureeritud'}
              online={park.bau_online}
              alarms={buildBauAlarmList(park)}
              groups={park.bau ? BAU_REG_GROUPS.map(g => ({
                ...g, values: park.bau,
              })) : []}
            />

            {/* Inverterid — iga konfigureeritud inverter eraldi sektsioonina */}
            {(park.inverters || []).map((inv, idx) => (
              <DeviceSection
                key={`inv-${idx}`}
                icon={<Radio size={14} style={{ color: '#0891b2' }} />}
                title={`Inverter — ${inv.type}${inv.label ? ` (${inv.label})` : ''}`}
                meta={`@ ${inv.host}:${inv.port ?? 502} · slave ${inv.slave_id} · FC 03`}
                online={inv._online}
                alarms={
                  inv._online === false
                    ? [{ severity: 'warning', text: `Inverter ei vasta päringutele (viimati: ${inv._updated_at || '—'})` }]
                    : !inv._updated_at
                    ? [{ severity: 'critical', text: 'Inverter ühendamata — andmeid pole InfluxDB-s' }]
                    : (inv.work_mode === 2 || inv.error_word
                       ? [{ severity: 'critical', text: `Fault mode või error_word ≠ 0 (error_word=0x${(inv.error_word || 0).toString(16)})` }]
                       : [])
                }
                groups={inverterRegGroups(inv).map(g => ({ ...g, values: inv || {} }))}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// Deye hübriid — registrid otse seadmest (GET /api/parks/:slot/deye-registers, AINULT lugemine)
function DeyeRegisterView({ slot }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`/api/parks/${slot}/deye-registers`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setData(j);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { setData(null); load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slot]);
  const cell = { padding: '4px 8px', borderBottom: '1px solid var(--divider)', fontSize: '0.72rem', textAlign: 'left' };
  const mono = { ...cell, fontFamily: 'JetBrains Mono, monospace' };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, fontSize: '0.72rem', color: 'var(--text-2)' }}>
        <span>
          Deye registrid loetakse otse inverterist (holding, FC 03, Solarman V5). Kaart: ha-solarman deye_p3.
          {error && <span style={{ color: 'var(--negative)', marginLeft: 8 }}>{error}</span>}
        </span>
        <button onClick={load} disabled={loading} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--border-strong)', background: 'white', fontSize: '0.7rem', cursor: 'pointer' }}>
          {loading ? 'Loen...' : 'Värskenda'}
        </button>
      </div>
      {!data && loading && <div style={{ padding: 20, color: 'var(--text-3)', fontSize: '0.75rem' }}>Loen inverterist...</div>}
      {(data?.devices || []).map((d) => (
        <div key={d.key} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 6 }}>
            Deye {d.label || d.key}
            <span style={{ fontSize: '0.66rem', color: 'var(--text-3)', fontWeight: 400, marginLeft: 8, fontFamily: 'JetBrains Mono, monospace' }}>
              {d.key}{d.logger_sn ? ` · logger SN ${d.logger_sn}` : ''}{d.read_at ? ` · ${new Date(d.read_at).toLocaleTimeString('et-EE')}` : ''}
            </span>
          </div>
          {d.error && <div style={{ color: 'var(--negative)', fontSize: '0.74rem' }}>Lugemine ebaõnnestus: {d.error}</div>}
          {(d.groups || []).map((g) => (
            <div key={g.group} style={{ marginBottom: 12, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ padding: '6px 10px', fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-2)', fontWeight: 600, background: 'var(--surface-2)' }}>
                {g.label}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup><col style={{ width: '14%' }} /><col style={{ width: '28%' }} /><col style={{ width: '36%' }} /><col style={{ width: '22%' }} /></colgroup>
                <thead><tr>
                  <th style={cell}>Register</th><th style={cell}>Toorväärtus</th><th style={cell}>Nimi</th><th style={{ ...cell, textAlign: 'right' }}>Väärtus</th>
                </tr></thead>
                <tbody>
                  {g.items.map((it, i) => (
                    <tr key={i}>
                      <td style={mono}>{it.regs.join(', ')}</td>
                      <td style={{ ...mono, color: 'var(--text-3)' }}>{it.raw.map((v) => `0x${(v ?? 0).toString(16).padStart(4, '0')}`).join(' ')}</td>
                      <td style={cell}>{it.name}</td>
                      <td style={{ ...mono, textAlign: 'right', fontWeight: 600 }}>{String(it.value)}{it.unit ? ` ${it.unit}` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function buildBauAlarmList(park) {
  if (!park?.bau) return [];
  const list = [];
  if (Array.isArray(park.bau.active_alarms)) {
    for (const a of park.bau.active_alarms) {
      list.push({
        severity: a.severity, text: a.name,
        meta: a.source === 'fault' ? `BAU fault · bit ${a.bit}` : `BAU L${a.level} · bit ${a.bit}`,
      });
    }
  }
  if (Array.isArray(park.bau.lc_active_faults)) {
    for (const f of park.bau.lc_active_faults) {
      list.push({
        severity: 'warning', text: f.name,
        meta: `Vedelikjahutus · ${f.addr}=${f.value}`,
      });
    }
  }
  if (park.bau.tempAndSmokeAlarm) {
    list.push({ severity: 'critical', text: 'Suitsu/temp alarm aktiivne', meta: 'Ohutus' });
  }
  return list;
}

function DeviceSection({ icon, title, meta, online, alarms, groups }) {
  const [expanded, setExpanded] = useState(true);
  const alarmCount = alarms.length;

  return (
    <div style={{
      marginBottom: 14, background: 'var(--surface)',
      border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden',
    }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          padding: '10px 14px', background: 'var(--surface-2)',
          border: 'none', borderBottom: expanded ? '1px solid var(--divider)' : 'none',
          cursor: 'pointer', fontFamily: 'inherit', gap: 8, flexWrap: 'wrap',
        }}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>{title}</span>
        <span style={{ fontSize: '0.62rem', color: 'var(--text-3)', fontFamily: 'JetBrains Mono, monospace' }}>
          {meta}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {online ? (
            <Badge color="var(--positive)" bg="rgba(21,128,61,0.08)" border="rgba(21,128,61,0.25)">● Online</Badge>
          ) : (
            <Badge color="var(--negative)" bg="rgba(220,38,38,0.06)" border="rgba(220,38,38,0.25)">○ Offline</Badge>
          )}
          {alarmCount > 0 && (
            <Badge color="var(--warning)" bg="rgba(180,83,9,0.08)" border="rgba(180,83,9,0.25)">⚠ {alarmCount} alarm{alarmCount === 1 ? '' : 'i'}</Badge>
          )}
        </span>
      </button>

      {expanded && (
        <div>
          <AlarmsBlock alarms={alarms} />
          {groups.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)', fontSize: '0.75rem' }}>
              Andmed puuduvad — seade pole konfigureeritud või ei vasta päringule.
            </div>
          )}
          {groups.map((g, i) => <RegisterGroup key={i} group={g} contextHeader={`${title} — ${meta}`} />)}
        </div>
      )}
    </div>
  );
}

function Badge({ children, color, bg, border }) {
  return (
    <span style={{
      fontSize: '0.6rem', padding: '2px 8px', borderRadius: 3,
      background: bg, color, border: `1px solid ${border}`,
    }}>
      {children}
    </span>
  );
}

function AlarmsBlock({ alarms }) {
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--divider)' }}>
      <div style={{
        fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'var(--text-3)', fontWeight: 500, marginBottom: 6,
      }}>
        Alarmid
      </div>
      {alarms.length === 0 ? (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: '0.7rem', color: 'var(--positive)',
          padding: '2px 9px', background: 'rgba(21,128,61,0.06)',
          border: '1px solid rgba(21,128,61,0.25)', borderRadius: 3,
        }}>
          <CheckCircle2 size={11} /> Aktiivseid alarme pole
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {alarms.map((a, i) => {
            const c = a.severity === 'critical' ? '#b91c1c' : a.severity === 'warning' ? '#b45309' : '#1d4ed8';
            const bg = a.severity === 'critical' ? 'rgba(220,38,38,0.06)' : a.severity === 'warning' ? 'rgba(180,83,9,0.06)' : 'rgba(29,78,216,0.05)';
            const Icon = a.severity === 'critical' ? AlertOctagon : a.severity === 'warning' ? AlertTriangle : Info;
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 10px', background: bg, borderRadius: 3,
                fontSize: '0.7rem', color: c,
              }}>
                <Icon size={12} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{a.text}</span>
                {a.meta && (
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', opacity: 0.7 }}>
                    {a.meta}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function fmtAddr(addr) {
  if (addr === null || addr === undefined) return '';
  return typeof addr === 'number' && addr > 255
    ? `0x${addr.toString(16).toUpperCase().padStart(4, '0')}`
    : `${addr}`;
}

function RwBadge({ rw }) {
  const isWrite = rw === 'R/W' || rw === 'W';
  return (
    <span style={{
      display: 'inline-block',
      padding: '0 5px', borderRadius: 2,
      fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.04em',
      color: isWrite ? '#b45309' : 'var(--text-3)',
      background: isWrite ? 'rgba(180,83,9,0.1)' : 'transparent',
      border: `1px solid ${isWrite ? 'rgba(180,83,9,0.3)' : 'var(--border)'}`,
      fontFamily: 'JetBrains Mono, monospace',
    }}>
      {rw || 'R'}
    </span>
  );
}

function copyAsCsv(group, contextHeader) {
  const header = ['Aadress', 'Nimi', 'Tüüp', 'Skaala', 'Ühik', 'R/W', 'Hetkeväärtus', 'Märkus'];
  const rows = group.regs.map(r => [
    fmtAddr(r.addr),
    r.key,
    r.type || '',
    r.scale != null ? String(r.scale) : '',
    r.unit || '',
    r.rw || 'R',
    fmtVal(group.values?.[r.key]),
    r.notes || '',
  ]);
  const lines = [
    `# ${contextHeader}`,
    `# ${group.name}  (${group.addr}, ${group.fc}, ${group.slave})`,
    header.join('\t'),
    ...rows.map(r => r.join('\t')),
  ];
  const csv = lines.join('\n');
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(csv);
  } else {
    // Fallback
    const ta = document.createElement('textarea');
    ta.value = csv;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
}

function RegisterGroup({ group, contextHeader }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    copyAsCsv(group, contextHeader);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ borderBottom: '1px solid var(--divider)' }}>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 14px', background: 'transparent',
          cursor: 'pointer',
        }}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span style={{ fontSize: '0.74rem', fontWeight: 500, color: 'var(--text)' }}>{group.name}</span>
        <span style={{ fontSize: '0.6rem', color: 'var(--text-4)', fontFamily: 'JetBrains Mono, monospace' }}>
          {group.addr} {group.fc ? `· ${group.fc}` : ''} {group.slave ? `· ${group.slave}` : ''}
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', color: 'var(--text-3)' }}>{group.regs.length} reg</span>
          <button
            onClick={handleCopy}
            style={{
              padding: '3px 8px', borderRadius: 3,
              border: '1px solid var(--border-strong)', background: copied ? '#15803d' : 'white',
              color: copied ? 'white' : 'var(--text-2)',
              cursor: 'pointer', fontSize: '0.62rem', fontFamily: 'inherit',
              fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 3,
            }}
            title="Kopeeri tabel CSV/TSV vormingus integraatorile saatmiseks"
          >
            {copied ? '✓ Kopeeritud' : '📋 Kopeeri'}
          </button>
        </span>
      </div>

      {open && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.68rem', fontFamily: 'JetBrains Mono, monospace' }}>
            <thead style={{ background: 'var(--surface-2)' }}>
              <tr style={{ borderBottom: '1px solid var(--divider)' }}>
                <th style={thStyle}>Aadress</th>
                <th style={thStyle}>Nimi</th>
                <th style={thStyle}>Tüüp</th>
                <th style={thStyle}>Skaala</th>
                <th style={thStyle}>Ühik</th>
                <th style={thStyle}>R/W</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Väärtus</th>
                <th style={thStyle}>Märkus</th>
              </tr>
            </thead>
            <tbody>
              {group.regs.map((r, i) => (
                <tr key={i} style={{ borderBottom: i < group.regs.length - 1 ? '1px solid var(--divider)' : 'none' }}>
                  <td style={{ ...tdStyle, color: 'var(--text)', fontWeight: 500 }}>{fmtAddr(r.addr)}</td>
                  <td style={{ ...tdStyle, color: 'var(--text)' }}>{r.key}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-3)' }}>{r.type || ''}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-3)' }}>{r.scale != null ? r.scale : ''}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-3)' }}>{r.unit || ''}</td>
                  <td style={tdStyle}><RwBadge rw={r.rw} /></td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: group.values?.[r.key] != null ? 'var(--text)' : 'var(--text-4)' }}>
                    {fmtVal(group.values?.[r.key])}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-3)', fontFamily: 'inherit', fontSize: '0.66rem', maxWidth: 280 }}>
                    {r.notes || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {group.notes && (
            <div style={{ padding: '6px 14px', fontSize: '0.62rem', color: 'var(--text-3)', fontStyle: 'italic', borderTop: '1px solid var(--divider)' }}>
              💡 {group.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '5px 14px', textAlign: 'left',
  fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em',
  color: 'var(--text-3)', fontWeight: 500,
};
const tdStyle = {
  padding: '4px 14px', color: 'var(--text-2)',
};

export default ModbusPage;
